import React, { useState, useEffect } from 'react';
import { Upload, FileText, Trash2, Download, Calendar, AlertCircle } from 'lucide-react';
import api from '../utils/api';
import { detectEnvironment } from '../utils/secretsManager';

// Get environment-specific bucket names
const getEnvironmentBuckets = () => {
  const env = detectEnvironment();
  
  // Handle the environment detection values
  // detectEnvironment returns: 'visiondev', 'visiontest', 'visionint', 'vision', 'localhost'
  let bucketSuffix = '';
  if (env === 'visiondev' || env === 'localhost') {
    bucketSuffix = '-dev';
  } else if (env === 'visiontest') {
    bucketSuffix = '-test';
  } else if (env === 'visionint') {
    bucketSuffix = '-int';
  }
  // 'vision' (prod) uses no suffix
  
  return {
    itops: `itopsadvisorkb${bucketSuffix}`,
    policy: `echostarpolicy-bedrock${bucketSuffix}`
  };
};

const KBUpload = () => {
  const [incidentFiles, setIncidentFiles] = useState([]);
  const [policyFiles, setPolicyFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Get environment-specific bucket names
  const buckets = getEnvironmentBuckets();

  // Load files on component mount
  useEffect(() => {
    loadFiles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFiles = async () => {
    setLoading(true);
    try {
      console.log('Loading KB files from buckets:', buckets);
      
      // Load ITOps Advisor files
      const itopsResponse = await api.listKBFiles(buckets.itops).catch(() => ({ files: [] }));
      console.log('ITOps response:', itopsResponse);
      setIncidentFiles(itopsResponse.files || []);

      // Load Company Policy files
      const policyResponse = await api.listKBFiles(buckets.policy).catch(() => ({ files: [] }));
      console.log('Policy response:', policyResponse);
      setPolicyFiles(policyResponse.files || []);
    } catch (error) {
      console.error('Error loading files:', error);
      setErrors({ load: 'Failed to load files. Please refresh the page.' });
    } finally {
      setLoading(false);
    }
  };

  const validateFile = (file) => {
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type - only PDF, DOCX, and TXT are allowed';
    }

    if (file.size > maxSize) {
      return `File size exceeds 10MB limit (${(file.size / (1024 * 1024)).toFixed(2)}MB)`;
    }

    return null;
  };

  const handleFileUpload = async (type, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setErrors({ [type]: validationError });
      return;
    }

    // Clear previous errors
    setErrors({ ...errors, [type]: null });

    // Use environment-specific bucket names
    const bucket = type === 'incident' ? buckets.itops : buckets.policy;
    const uploadId = `${type}-${Date.now()}`;

    try {
      // Get presigned URL
      setUploadProgress({ ...uploadProgress, [uploadId]: 0 });
      const presignedResponse = await api.getPresignedUploadUrl(
        bucket,
        file.name,
        file.type,
        file.size
      );

      // Upload file to S3 using presigned URL
      await uploadFileToS3(presignedResponse, file, (progress) => {
        setUploadProgress({ ...uploadProgress, [uploadId]: progress });
      });

      // Clear progress
      setUploadProgress({ ...uploadProgress, [uploadId]: undefined });

      // Reload files
      await loadFiles();

      // Reset file input
      event.target.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      setErrors({ [type]: error.message || 'Upload failed - please try again' });
      setUploadProgress({ ...uploadProgress, [uploadId]: undefined });
    }
  };

  const uploadFileToS3 = (presignedData, file, onProgress) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      
      // Add all presigned fields
      Object.keys(presignedData.fields).forEach(key => {
        formData.append(key, presignedData.fields[key]);
      });
      
      // Add file last
      formData.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error - please check your connection'));
      });

      xhr.open('POST', presignedData.url);
      xhr.send(formData);
    });
  };

  const handleDelete = async (type, fileKey) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    // Use environment-specific bucket names
    const bucket = type === 'incident' ? buckets.itops : buckets.policy;

    try {
      await api.deleteKBFile(bucket, fileKey);
      await loadFiles();
    } catch (error) {
      console.error('Delete error:', error);
      setErrors({ [type]: 'Failed to delete file. Please try again.' });
    }
  };

  const handleDownload = async (type, fileKey) => {
    // Use environment-specific bucket names
    const bucket = type === 'incident' ? buckets.itops : buckets.policy;

    try {
      const response = await api.getPresignedDownloadUrl(bucket, fileKey);
      window.open(response.url, '_blank');
    } catch (error) {
      console.error('Download error:', error);
      setErrors({ [type]: 'Failed to download file. Please try again.' });
    }
  };

  const renderFileList = (files, type, color) => {
    if (loading) {
      return <div className="text-text-muted text-sm">Loading files...</div>;
    }

    if (files.length === 0) {
      return <div className="text-text-muted text-sm">No documents uploaded yet</div>;
    }

    return files.map((file, idx) => (
      <div key={idx} className={`flex items-center justify-between p-3 bg-dark-border/40 rounded-lg border border-border/50 hover:border-${color}-accent/40 hover:bg-${color}-accent/5 transition-all`}>
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <FileText size={18} className={`text-${color}-accent flex-shrink-0`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-text-primary truncate">{file.name}</p>
            <div className="flex items-center space-x-3 text-xs text-text-muted">
              <span>{file.sizeFormatted}</span>
              <span>•</span>
              <span className="flex items-center space-x-1">
                <Calendar size={12} />
                <span>{new Date(file.uploadedAt).toLocaleDateString()}</span>
              </span>
              <span>•</span>
              <span>{file.uploadedBy}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0 ml-3">
          <button 
            onClick={() => handleDownload(type, file.key)}
            className="p-2 hover:bg-blue-500/20 rounded transition-colors"
            title="Download"
          >
            <Download size={16} className="text-blue-400" />
          </button>
          <button 
            onClick={() => handleDelete(type, file.key)}
            className={`p-2 hover:bg-${color}-accent/20 rounded transition-colors`}
            title="Delete"
          >
            <Trash2 size={16} className={`text-${color}-accent`} />
          </button>
        </div>
      </div>
    ));
  };

  const renderUploadProgress = (uploadId) => {
    const progress = uploadProgress[uploadId];
    if (progress === undefined) return null;

    return (
      <div className="mt-2">
        <div className="flex items-center justify-between text-xs text-text-secondary mb-1">
          <span>Uploading...</span>
          <span>{progress}%</span>
        </div>
        <div className="w-full bg-dark-border rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  };

  const renderError = (type) => {
    const error = errors[type];
    if (!error) return null;

    return (
      <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start space-x-2">
        <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 relative">

<div className="mb-6 relative z-10">
        <h2 className="text-2xl font-bold text-blue-400 mb-1 tracking-tight">
          KB Upload
        </h2>
        <p className="text-sm text-text-muted">Upload documents to knowledge bases</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative z-10">
        {/* ITOps Advisor Upload */}
        <div className="bg-surface border border-border rounded-lg p-6 relative overflow-hidden group hover:border-blue-500/40 transition-all"
             >
          <div className="absolute inset-0 bg-gradient-to-br from-orange-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          
          <div className="flex items-center space-x-3 mb-6 relative z-10">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center border border-blue-500/30"
                 >
              <Upload size={20} className="text-blue-400"  />
            </div>
            <div>
              <h3 className="text-lg font-bold text-text-primary">
                ITOps Advisor KB
              </h3>
              <p className="text-xs text-text-muted">{incidentFiles.length} documents</p>
            </div>
          </div>

          <div className="mb-6 relative z-10">
            <label className="block w-full cursor-pointer">
              <div className="border-2 border-dashed border-blue-500/40 rounded-lg p-8 text-center hover:border-blue-500/60 hover:bg-blue-500/5 transition-all"
                   >
                <Upload size={32} className="mx-auto mb-3 text-blue-400"  />
                <p className="text-sm font-medium text-text-primary mb-1">Click to upload or drag and drop</p>
                <p className="text-xs text-text-muted">PDF, DOCX, TXT (max 10MB)</p>
              </div>
              <input 
                type="file" 
                className="hidden" 
                accept=".pdf,.docx,.txt"
                onChange={(e) => handleFileUpload('incident', e)} 
              />
            </label>
            {renderUploadProgress('incident')}
            {renderError('incident')}
          </div>

          <div className="space-y-2 relative z-10">
            <h4 className="text-sm font-semibold text-text-secondary mb-3">Current Documents</h4>
            {renderFileList(incidentFiles, 'incident', 'orange')}
          </div>
        </div>

        {/* Company Policy Upload */}
        <div className="bg-surface border border-border rounded-lg p-6 relative overflow-hidden group hover:border-blue-500/40 transition-all"
             >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          
          <div className="flex items-center space-x-3 mb-6 relative z-10">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center border border-blue-500/30"
                 >
              <Upload size={20} className="text-blue-400"  />
            </div>
            <div>
              <h3 className="text-lg font-bold text-text-primary">
                Company Policy KB
              </h3>
              <p className="text-xs text-text-muted">{policyFiles.length} documents</p>
            </div>
          </div>

          <div className="mb-6 relative z-10">
            <label className="block w-full cursor-pointer">
              <div className="border-2 border-dashed border-blue-500/40 rounded-lg p-8 text-center hover:border-blue-500/60 hover:bg-blue-500/5 transition-all"
                   >
                <Upload size={32} className="mx-auto mb-3 text-blue-400"  />
                <p className="text-sm font-medium text-text-primary mb-1">Click to upload or drag and drop</p>
                <p className="text-xs text-text-muted">PDF, DOCX, TXT (max 10MB)</p>
              </div>
              <input 
                type="file" 
                className="hidden" 
                accept=".pdf,.docx,.txt"
                onChange={(e) => handleFileUpload('policy', e)} 
              />
            </label>
            {renderUploadProgress('policy')}
            {renderError('policy')}
          </div>

          <div className="space-y-2 relative z-10">
            <h4 className="text-sm font-semibold text-text-secondary mb-3">Current Documents</h4>
            {renderFileList(policyFiles, 'policy', 'purple')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KBUpload;
